// ─────────────────────────────────────────────────────────────
// expenseReport.config.js — Report 6: expense report
// Shape: statement (portrait) — entry list + category summary
// Data:  already held by ExpensesTab
//        { rows, totals: {entry_count,total}, by_category }
//
// HR-posted rows are included. They are a real cost of running
// the tape business and the P&L would understate without them,
// but they are marked so the reader knows they cannot be
// corrected from this module.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const expenseReportConfig = ({ data, filters, categories }) => {
  const rows = (data.rows || []).map((r) => ({
    date: r.expense_date,
    category: r.category_name,
    paid_to: r.paid_to,
    ref_no: r.ref_no,
    source: r.source === 'manual' ? 'Manual' : 'HR',
    amount: r.amount,
  }));

  const total = num(data.totals?.total);
  const entries = num(data.totals?.entry_count);

  const byCategory = (data.by_category || []).map((c) => ({
    category: c.category_name,
    entry_count: c.entry_count,
    total: c.total,
    share: total > 0 ? (num(c.total) / total) * 100 : 0,
  }));

  const selected = (categories || []).find(
    (c) => String(c.id) === String(filters.category_id)
  );

  const hrTotal = (data.rows || [])
    .filter((r) => r.source !== 'manual')
    .reduce((s, r) => s + num(r.amount), 0);

  return {
    kind: 'statement',
    orientation: 'p',
    title: 'Expense Report',
    subtitle: `${FORMATTERS.date(filters.from_date)}  to  ${FORMATTERS.date(filters.to_date)}`,

    boxes: [
      {
        heading: 'Report Filters',
        rows: [
          { label: 'Category', value: selected ? selected.name : 'All Categories' },
          { label: 'From Date', value: FORMATTERS.date(filters.from_date) },
          { label: 'To Date', value: FORMATTERS.date(filters.to_date) },
        ],
      },
      {
        heading: 'Summary',
        rows: [
          {
            label: 'Entries',
            value: FORMATTERS.int(entries),
            label2: 'Categories',
            value2: byCategory.length,
          },
          { label: 'Manual', value: FORMATTERS.money(total - hrTotal) },
          { label: 'From HR', value: FORMATTERS.money(hrTotal) },
          { label: 'Total Spent', value: FORMATTERS.money(total) },
        ],
      },
    ],

    sections: [
      {
        title: 'Where It Went',
        tone: 'expense',
        columns: [
          { key: 'category', label: 'Category', align: 'left', fmt: 'text' },
          { key: 'entry_count', label: 'Entries', align: 'right', fmt: 'int', width: 22 },
          { key: 'share', label: 'Share', align: 'right', fmt: 'pct', width: 22 },
          { key: 'total', label: 'Amount', align: 'right', fmt: 'money', width: 30, bold: true },
        ],
        rows: byCategory,
        footRow: [
          'TOTAL',
          FORMATTERS.int(entries),
          '100%',
          FORMATTERS.money(total),
        ],
      },
      {
        title: 'Expense Entries',
        tone: 'expense',
        columns: [
          { key: 'date', label: 'Date', align: 'left', fmt: 'date', width: 24 },
          { key: 'category', label: 'Category', align: 'left', fmt: 'text', width: 32 },
          { key: 'paid_to', label: 'Paid To', align: 'left', fmt: 'text' },
          { key: 'ref_no', label: 'Reference', align: 'left', fmt: 'text', width: 26 },
          { key: 'source', label: 'Source', align: 'center', fmt: 'text', width: 18 },
          { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28, bold: true },
        ],
        rows,
        footRow: ['TOTAL', '', '', '', '', FORMATTERS.money(total)],
      },
    ],

    totals: [
      { label: 'Manual Entries', value: total - hrTotal, fmt: 'money' },
      { label: 'HR Allocations', value: hrTotal, fmt: 'money' },
      { label: 'Total Expenses', value: total, fmt: 'money', emphasis: true, tone: 'expense' },
    ],

    signatures: SIGNATURES.report,
  };
};

export default expenseReportConfig;